/**
 * Kiosk employee lookup: type the full employee number, get your name back.
 * The kiosk never holds the directory; it asks for one exact number (HMH-2415).
 */

import { useState } from 'react';
import { lookupEmployeeByNumber } from '../../hooks/useBreakfast';

interface EmployeeNumberLookupProps {
  onFound: (displayName: string, employeeNumber: string) => void;
}

const MESSAGES = {
  none: "We couldn't find that employee number. Check it and try again.",
  limited: 'Too many tries. Please wait a minute and try again.',
  error: 'Lookup is unavailable right now. Please try again.',
} as const;

export function EmployeeNumberLookup({ onFound }: EmployeeNumberLookupProps) {
  const [number, setNumber] = useState('');
  const [isLooking, setIsLooking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = number.trim();
    if (!trimmed || isLooking) return;
    setIsLooking(true);
    setMessage(null);
    const result = await lookupEmployeeByNumber(trimmed);
    setIsLooking(false);
    if (result.status === 'found') {
      onFound(result.displayName, trimmed);
    } else {
      setMessage(MESSAGES[result.status]);
    }
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        type="text"
        value={number}
        onChange={(e) => {
          setNumber(e.target.value);
          setMessage(null);
        }}
        placeholder="Employee number"
        aria-label="Employee number"
        className="w-full px-8 py-6 text-4xl text-center tracking-widest bg-gray-100 border-0 rounded-2xl
                   text-gray-900 placeholder-gray-400 focus:ring-4 focus:ring-[#10348e]/30 focus:outline-none
                   h-24"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={32}
        autoFocus
      />

      {message && (
        <div role="alert" className="text-center text-xl text-red-600">
          {message}
        </div>
      )}

      <button
        type="submit"
        disabled={!number.trim() || isLooking}
        className="w-full h-24 bg-[#10348e] text-white text-3xl font-medium rounded-2xl
                   hover:bg-[#0d2b73] active:scale-[0.98] active:bg-[#0a2260] transition-all
                   disabled:opacity-50 disabled:active:scale-100"
      >
        {isLooking ? 'Looking up…' : 'Continue'}
      </button>
    </form>
  );
}
